import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token || !email) {
        setValidating(false);
        return;
      }

      try {
        await axios.post(`${API}/auth/verify-reset-token?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`);
        setTokenValid(true);
      } catch (error) {
        console.error('Token validation failed:', error);
        setTokenValid(false);
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token, email]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/auth/reset-password`, {
        email,
        resetToken: token,
        newPassword: formData.newPassword
      });
      
      toast.success('Password reset successfully!');
      setResetComplete(true);
    } catch (error) {
      console.error('Password reset error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to reset password';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Loading state while validating token
  if (validating) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50 px-4 py-8">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <i className="fas fa-spinner fa-spin text-4xl text-emerald-600 mb-4"></i>
            <p className="text-gray-600">Validating reset link...</p>
          </CardContent>
        </Card>
      </main>
    );
  }

  // Invalid or missing token
  if (!token || !email || !tokenValid) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50 px-4 py-8">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center text-red-600">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              Invalid Reset Link
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-600">
              This password reset link is invalid or has expired.
            </p>
            <p className="text-sm text-gray-500">
              Reset links are valid for 1 hour. Please request a new one.
            </p>
            <Button 
              onClick={() => navigate('/auth')}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  // Reset complete
  if (resetComplete) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50 px-4 py-8">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center text-emerald-600">
              <i className="fas fa-check-circle mr-2"></i>
              Password Reset Complete
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-600">
              Your password has been successfully reset.
            </p>
            <p className="text-sm text-gray-500">
              You can now log in with your new password.
            </p>
            <Button 
              onClick={() => navigate('/auth')}
              className="bg-emerald-600 hover:bg-emerald-700 w-full"
              data-testid="go-to-login-btn"
            >
              <i className="fas fa-sign-in-alt mr-2"></i>
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  // Reset password form
  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50 px-4 py-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            <i className="fas fa-lock text-emerald-600 mr-2"></i>
            Set New Password
          </CardTitle>
          <p className="text-center text-gray-600">
            Enter your new password below
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="newPassword">New Password *</Label>
              <Input
                id="newPassword"
                type="password"
                value={formData.newPassword}
                onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                placeholder="••••••••"
                required
                data-testid="new-password-input"
              />
              <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
            </div>

            <div>
              <Label htmlFor="confirmPassword">Confirm Password *</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="••••••••"
                required
                data-testid="confirm-password-input"
              />
            </div>

            {/* Password match indicator */}
            {formData.newPassword && formData.confirmPassword && (
              <div className={`text-sm flex items-center gap-2 ${
                formData.newPassword === formData.confirmPassword 
                  ? 'text-emerald-600' 
                  : 'text-red-600'
              }`}>
                <i className={`fas ${
                  formData.newPassword === formData.confirmPassword 
                    ? 'fa-check-circle' 
                    : 'fa-times-circle'
                }`}></i>
                {formData.newPassword === formData.confirmPassword 
                  ? 'Passwords match' 
                  : 'Passwords do not match'
                }
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full bg-emerald-600 hover:bg-emerald-700" 
              disabled={loading || formData.newPassword !== formData.confirmPassword}
              data-testid="reset-password-btn"
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Resetting...
                </>
              ) : (
                <>
                  <i className="fas fa-key mr-2"></i>
                  Reset Password
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t text-center">
            <button
              type="button"
              onClick={() => navigate('/auth')}
              className="text-emerald-600 hover:underline text-sm"
            >
              <i className="fas fa-arrow-left mr-1"></i>
              Back to Login
            </button>
          </div>
        </CardContent>
      </Card>
    </main>
  );
};

export default ResetPassword;
